import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";

const BASE = `${API_URL}/morning-dash/admin`;

const SECTIONS = [
  {
    title: "Operating window (Africa/Harare time)",
    fields: [
      { key: "window_start", label: "Window opens", type: "time", help: "Passengers can request Morning Dash from this time." },
      { key: "window_end", label: "Window closes", type: "time", help: "No Morning Dash run can run past this time." },
      { key: "latest_departure", label: "Latest departure", type: "time", help: "The latest time a run may leave. The last request time is this minus the formation time below." },
      { key: "formation_minutes", label: "Formation time (minutes)", type: "number", step: 1, help: "How long a run has to reach the minimum passengers. If it does not, the run is cancelled, held seats expire and nobody is charged." },
    ],
  },
  {
    title: "Seats",
    fields: [
      { key: "min_passengers", label: "Minimum passengers", type: "number", step: 1, help: "A run is confirmed (details shared, commission charged) once this many passengers are held. Cannot be above the maximum." },
      { key: "max_passengers", label: "Maximum passengers", type: "number", step: 1, help: "Seats offered per run (1 to 6). Three rear seats plus one front seat is 4." },
    ],
  },
  {
    title: "Pickup and drop-off rules",
    fields: [
      { key: "detour_km", label: "Maximum detour per passenger (km)", type: "number", step: 0.1, help: "How far off his route a driver may go to pick up an extra passenger. Applies to Morning Dash only." },
      { key: "snap_max_km", label: "Maximum distance to a drop-off point (km)", type: "number", step: 0.5, help: "If the passenger's destination is further than this from every active drop-off point, Morning Dash is not offered." },
      { key: "nearby_radius_km", label: "Driver request radius (km)", type: "number", step: 0.5, help: "Drivers see open Morning Dash requests within this distance of their location." },
    ],
  },
  {
    title: "No-shows",
    fields: [
      { key: "no_show_wait_minutes", label: "Wait before a no-show (minutes)", type: "number", step: 1, help: "After the driver marks arrival, he must wait this long before he can mark a passenger as a no-show. Commission is not refunded after a no-show." },
    ],
  },
];

const NUMERIC = ["formation_minutes", "min_passengers", "max_passengers", "detour_km", "snap_max_km", "nearby_radius_km", "no_show_wait_minutes"];

const toMinutes = (t) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t || "");
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : NaN;
};
const fmt = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

function Field({ field, value, onChange }) {
  return (
    <div className="mb-4">
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{field.label}</label>
      <input
        type={field.type}
        step={field.step}
        min={field.type === "number" ? 0 : undefined}
        value={value ?? ""}
        onChange={(e) => onChange(field.key, e.target.value)}
        className="w-full rounded-lg border p-2"
      />
      <p className="mt-1 text-xs text-gray-500">{field.help}</p>
    </div>
  );
}

const emptyPoint = { point_id: null, name: "", lat: "", lng: "", active: true, sort_order: 0 };

export default function MorningDash() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null);
  const [pointMessage, setPointMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await (await fetch(`${BASE}/overview`)).json();
      if (!res.success) throw new Error(res.message || "Failed to load");
      setData(res.data);
      setForm(res.data.settings);
    } catch (e) {
      setMessage(`Failed to load: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const change = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const call = async (url, method, body) => {
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.message || "Request failed");
    return json.data;
  };

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const body = { ...form };
      NUMERIC.forEach((k) => { body[k] = body[k] === "" ? body[k] : Number(body[k]); });
      const result = await call(`${BASE}/settings`, "PUT", body);
      setData(result);
      setForm(result.settings);
      setMessage(result.changed.length ? `Saved: ${result.changed.join(", ")}. Changes apply immediately.` : "Nothing changed.");
    } catch (e) {
      setMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  const savePoint = async () => {
    setPointMessage("");
    try {
      const body = { name: editing.name, lat: editing.lat, lng: editing.lng, active: editing.active, sort_order: editing.sort_order };
      const points = editing.point_id
        ? await call(`${BASE}/points/${editing.point_id}`, "PUT", body)
        : await call(`${BASE}/points`, "POST", body);
      setData((prev) => ({ ...prev, points }));
      setEditing(null);
    } catch (e) {
      setPointMessage(e.message);
    }
  };

  if (!form || !data) {
    return (
      <DashboardLayout>
        <div className="p-6">{message || "Loading..."}</div>
      </DashboardLayout>
    );
  }

  const latest = toMinutes(form.latest_departure);
  const windowEnd = toMinutes(form.window_end);
  const formation = Number(form.formation_minutes);
  const lastRequest = Number.isFinite(latest) && Number.isFinite(windowEnd) && Number.isFinite(formation) ? fmt(Math.min(latest, windowEnd) - formation) : "-";
  const activePoints = data.points.filter((p) => p.active).length;
  const today = data.today;

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-4xl p-6">
        <h2 className="mb-1 text-2xl font-bold text-gray-800 dark:text-white">Morning Dash</h2>
        <p className="mb-6 text-sm text-gray-500">
          A shared morning ride to fixed CBD drop-off points. Passengers choose their own pickup, drivers accept passengers one by one, and a seat stays
          <strong> Reserved </strong> (no details shared, no commission) until the minimum number of passengers is held. Then everyone is <strong>Accepted</strong> together
          and commission is charged. If the run does not fill in time, it is cancelled at no cost.
        </p>

        <div className="mb-6 flex items-center justify-between rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Morning Dash is {form.enabled ? "ON" : "OFF"}</h3>
            <p className="text-xs text-gray-500">
              The kill switch. When off, passengers are not offered Morning Dash and drivers do not see its requests. Runs already accepted are not cancelled by switching it off.
              {activePoints === 0 ? " Add and activate at least one drop-off point before turning it on." : ""}
            </p>
          </div>
          <button
            onClick={() => change("enabled", !form.enabled)}
            className={`rounded-lg px-4 py-2 text-white ${form.enabled ? "bg-green-600" : "bg-gray-500"}`}
          >
            {form.enabled ? "On (click to turn off)" : "Off (click to turn on)"}
          </button>
        </div>

        {SECTIONS.map((section) => (
          <div key={section.title} className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">{section.title}</h3>
            {section.fields.map((f) => (
              <Field key={f.key} field={f} value={form[f.key]} onChange={change} />
            ))}
            {section.title.startsWith("Operating") && (
              <p className="rounded bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                Last time a passenger can request: <strong>{lastRequest}</strong> (latest departure minus formation time)
              </p>
            )}
          </div>
        ))}

        {message && <p className="mb-4 text-sm text-gray-700 dark:text-gray-200">{message}</p>}
        <button onClick={save} disabled={saving} className="mb-8 rounded-lg bg-blue-600 px-5 py-2 text-white disabled:opacity-50">
          {saving ? "Saving..." : "Save settings"}
        </button>

        <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Drop-off points</h3>
            <button onClick={() => { setEditing({ ...emptyPoint }); setPointMessage(""); }} className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white">Add point</button>
          </div>
          <p className="mb-3 text-xs text-gray-500">
            Passengers are always dropped at the nearest active point to the destination they chose. Points are never deleted (past requests refer to them) — switch a point off instead.
            Names containing “placeholder” are sample points: replace the name and coordinates before the pilot.
          </p>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-xs text-gray-500">
                <th className="py-2">Name</th><th>Latitude</th><th>Longitude</th><th>Order</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {data.points.map((p) => (
                <tr key={p.point_id} className="border-b">
                  <td className="py-2">{p.name}</td>
                  <td>{p.lat}</td>
                  <td>{p.lng}</td>
                  <td>{p.sort_order}</td>
                  <td>{p.active ? "Active" : "Off"}</td>
                  <td><button className="text-blue-600 underline" onClick={() => { setEditing({ ...p }); setPointMessage(""); }}>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          {editing && (
            <div className="mt-4 rounded-lg border bg-gray-50 p-4 dark:bg-gray-700">
              <h4 className="mb-3 font-semibold text-gray-800 dark:text-white">{editing.point_id ? "Edit drop-off point" : "New drop-off point"}</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <input placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="col-span-2 rounded border p-2" />
                <input placeholder="Latitude (e.g. -17.8292)" value={editing.lat} onChange={(e) => setEditing({ ...editing, lat: e.target.value })} className="rounded border p-2" />
                <input placeholder="Longitude (e.g. 31.0522)" value={editing.lng} onChange={(e) => setEditing({ ...editing, lng: e.target.value })} className="rounded border p-2" />
                <input placeholder="Order" type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} className="rounded border p-2" />
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active
                </label>
              </div>
              {pointMessage && <p className="mt-2 text-sm text-red-600">{pointMessage}</p>}
              <div className="mt-3 flex gap-2">
                <button onClick={savePoint} className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm text-white">Save point</button>
                <button onClick={() => setEditing(null)} className="rounded-lg border px-4 py-1.5 text-sm">Cancel</button>
              </div>
            </div>
          )}
        </div>

        <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white">Today ({today.date})</h3>
          <p className="mb-3 text-xs text-gray-500">Live counts from the database. Refresh the page to update.</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="mb-1 font-medium">Passenger requests</p>
              {Object.keys(today.requests).length ? Object.entries(today.requests).map(([s, n]) => <p key={s}>{s}: {n}</p>) : <p className="text-gray-500">None today</p>}
            </div>
            <div>
              <p className="mb-1 font-medium">Driver runs</p>
              {today.runs.length ? today.runs.map((r, i) => <p key={i}>{r.status}{r.confirmed ? " (confirmed)" : ""}: {r.count}</p>) : <p className="text-gray-500">None today</p>}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
